import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { broadcasts, contacts, channelWa, templates, sms } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert, Stepper, PageHeader, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { usePhoneDefaultCountry } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { normalisePhone } from '@repletoryreach/sdk-utils/phone';
import { formatTime } from '@repletoryreach/sdk-utils/date';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { useUnsavedGuard } from '../../../lib/useUnsavedGuard';
import { useEmailStatus } from '../../../lib/email';
import { parseComponents, extractVariables } from '../../../lib/template-components';
import {
  EMPTY_DRAFT, MAX_PASTED_CHARS, fromSaved, toSaved, hasContent,
  isMediaFormat, scheduledInstant, looksLikeEmail, isButtonLink, sendsPerLanguage,
  type BroadcastDraftState
} from './draft';
import { AudienceStep } from './components/AudienceStep';
import { MessageStep } from './components/MessageStep';
import { SmsMessageStep } from './components/SmsMessageStep';
import { EmailMessageStep } from './components/EmailMessageStep';
import { WhenStep } from './components/WhenStep';
import { ReviewStep } from './components/ReviewStep';
import { startErrorMessage, isDailyLimit } from '../components/startError';
import './new.css';

const STEPS = ['audience', 'message', 'when', 'review'] as const;
const LAST = STEPS.length - 1;

/** How long after the last keystroke the draft is written. */
const DRAFT_DEBOUNCE_MS = 1200;

/** How long after the last keystroke an SMS text is measured and previewed. */
const SMS_DEBOUNCE_MS = 400;

/**
 * Creating a broadcast, as a page with steps.
 *
 * It used to be a drawer over the list: three cramped steps in a 480px column,
 * with the audience, the message and the schedule all in a scroll. That is the
 * wrong shape for the most consequential thing this console does. A broadcast
 * spends real money, reaches real people and cannot be recalled once it goes,
 * so it gets a route of its own, room to show the audience it will hit, and a
 * last step that is the confirmation rather than a step followed by an "are you
 * sure" dialog on top of it.
 *
 * Four questions, in the order a person actually asks them:
 *
 * 1. **Audience.** Who, how many of them can still be messaged, and how many
 *    have opted out and are therefore excluded. That last number surprises
 *    people, so it is stated rather than quietly subtracted.
 * 2. **Message.** Which approved template, and what goes in each placeholder,
 *    with a preview built from a real contact out of the audience.
 * 3. **When.** Now, or a time, in the organisation's own timezone, named.
 * 4. **Review and send.** The count, the cost in credits, the balance, and what
 *    happens if the wallet runs dry part-way. Send lives here and nowhere else.
 *
 * The browser validates continuously and the server decides. Every count, every
 * price and every refusal on this page comes from `POST /v1/broadcasts/preview`,
 * which runs the same audience resolution the create route runs and asks
 * billing the same question the start route asks. So Send is offered only when
 * the server has already said it would accept it.
 */
export default function NewBroadcastPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const defaultCountry = usePhoneDefaultCountry();

  const [state, setState] = useState<BroadcastDraftState>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  /** When the server last acknowledged a draft write, and when the form last changed. */
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [changedAt, setChangedAt] = useState<number | null>(null);
  /** True once a draft write has failed: the page then guards on leave instead. */
  const [draftBroken, setDraftBroken] = useState(false);
  const loadedDraft = useRef(false);

  const timezone = user?.org?.timezone || 'Africa/Johannesburg';

  const set = useCallback(<K extends keyof BroadcastDraftState>(key: K, value: BroadcastDraftState[K]) => {
    setState((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
    setChangedAt(Date.now());
  }, []);

  // ── What the wizard needs to offer choices ──────────────────────────────

  const numbersQ = useQuery({ queryKey: ['phone-numbers'], queryFn: () => channelWa.listPhoneNumbers() });
  const templatesQ = useQuery({ queryKey: ['templates'], queryFn: () => templates.list() });
  const segmentsQ = useQuery({ queryKey: ['segments'], queryFn: () => contacts.listSegments() });
  const tagsQ = useQuery({ queryKey: ['contacts', 'tags'], queryFn: () => contacts.listTags() });

  /*
   * SMS (4 October 2026). Offered only while the platform says it can send
   * one: when it cannot, there is no channel choice at all and the wizard is
   * the WhatsApp wizard it always was. A draft saved as SMS while SMS was
   * available reads as WhatsApp if it has since gone away.
   */
  const smsStatusQ = useQuery({
    queryKey: ['sms', 'status'],
    queryFn: () => sms.getStatus(),
    retry: false,
    staleTime: 60_000
  });
  const smsAvailable = smsStatusQ.data?.data?.available === true;
  const isSms = smsAvailable && state.channel === 'sms';
  /** The SMS text once the typing has paused: what the counter and the preview are asked about. */
  const smsText = useDebounce(state.smsBody, SMS_DEBOUNCE_MS).trim();

  const smsCountQ = useQuery({
    enabled: isSms && smsText.length > 0,
    queryKey: ['sms', 'preview', smsText, state.smsKind],
    queryFn: () => sms.preview({ text: smsText, marketing: state.smsKind === 'marketing' }),
    placeholderData: (prev) => prev,
    retry: false,
    staleTime: 60_000
  });
  const smsCount = isSms && smsText.length > 0 ? smsCountQ.data?.data ?? null : null;

  /*
   * Email (4 October 2026), on the same terms as SMS: offered only while the
   * platform says it can send one, and a draft saved as email reads as
   * WhatsApp if email has since gone away. The subject, the text and the
   * button are previewed once the typing has paused.
   */
  const { available: emailAvailable } = useEmailStatus();
  const isEmail = emailAvailable && state.channel === 'email';
  const emailSubject = useDebounce(state.emailSubject, SMS_DEBOUNCE_MS).trim();
  const emailBody = useDebounce(state.emailBody, SMS_DEBOUNCE_MS).trim();
  const emailButtonLabel = useDebounce(state.emailButtonLabel, SMS_DEBOUNCE_MS).trim();
  const emailButtonUrl = useDebounce(state.emailButtonUrl, SMS_DEBOUNCE_MS).trim();
  /** Whether the preview is being asked about the email as it stands, not as it stood a moment ago. */
  const emailSettled = emailSubject === state.emailSubject.trim() && emailBody === state.emailBody.trim() &&
    emailButtonLabel === state.emailButtonLabel.trim() && emailButtonUrl === state.emailButtonUrl.trim();
  /** The button goes with the preview only when it is whole: a label and a link the server would take. */
  const emailButton = emailButtonLabel && isButtonLink(emailButtonUrl)
    ? { email_button_label: emailButtonLabel, email_button_url: emailButtonUrl }
    : {};

  // Numbers taken out of service cannot send, so they are not offered.
  const numbers = useMemo(
    () => (numbersQ.data?.data ?? []).filter((n) => n.is_active),
    [numbersQ.data]
  );

  /*
   * Only a template Meta has actually approved may be broadcast.
   *
   * `dev_fallback` marks one auto-approved by the poller outside production;
   * the SDK is explicit that such a template "must be treated as unapproved for
   * any real send", so it is not offered and the reason is said out loud rather
   * than leaving the list mysteriously short.
   */
  const sendable = useMemo(
    () => (templatesQ.data?.data ?? []).filter((tpl) => tpl.status === 'approved' && !tpl.dev_fallback),
    [templatesQ.data]
  );
  const blockedByDevFallback = useMemo(
    () => (templatesQ.data?.data ?? []).filter((tpl) => tpl.status === 'approved' && tpl.dev_fallback).length,
    [templatesQ.data]
  );

  /*
   * A template is approved per language, one row each, so the choice is a code and a version. A draft saved before
   * the version was kept names only the code, and takes that code's first approved version.
   */
  const template = sendable.find((tpl) => tpl.code === state.templateCode && tpl.language === state.templateLanguage)
    ?? sendable.find((tpl) => tpl.code === state.templateCode)
    ?? null;
  /** Every approved version of the chosen template, by Meta's language code. */
  const versions = useMemo(
    () => Array.from(new Set(sendable.filter((tpl) => tpl.code === state.templateCode).map((tpl) => tpl.language))),
    [sendable, state.templateCode]
  );
  /**
   * Send each person the version in their language (1 November 2026): on by default when the template is approved
   * in more than one language. WhatsApp only; SMS and email have no template.
   */
  const perLanguage = !isSms && !isEmail && sendsPerLanguage(state, versions.length);
  const parsed = useMemo(() => parseComponents(template?.components), [template?.components]);
  const vars = useMemo(() => extractVariables(parsed).map(String), [parsed]);
  const mediaHeader = parsed.find((c) => c.type === 'HEADER' && isMediaFormat(c.format)) ?? null;

  /*
   * Every pasted line goes through the one phone-number rule with the
   * organisation's default country, so 0615360809 reaches the send as
   * +27615360809 and a line that cannot be read is named rather than sent.
   */
  const pasted = useMemo(() => {
    const ok = new Set<string>();
    const bad: string[] = [];
    for (const line of state.recipients.split(/[\n,;]/).map((s) => s.trim()).filter(Boolean)) {
      // For email, a pasted list is addresses, one per line (4 October 2026).
      if (isEmail) {
        if (looksLikeEmail(line)) ok.add(line.toLowerCase()); else bad.push(line);
        continue;
      }
      const r = normalisePhone(line, defaultCountry);
      if (r.ok) ok.add(r.e164); else bad.push(line);
    }
    return { ok: Array.from(ok), bad };
  }, [state.recipients, defaultCountry, isEmail]);

  /** The audience as the API takes it: named, never listed, except a paste. */
  const audienceBody = useMemo(() => {
    if (state.audienceMode === 'tag') return state.contactTag ? { contact_tag: state.contactTag } : null;
    if (state.audienceMode === 'segment') return state.segmentId ? { segment_id: state.segmentId } : null;
    return pasted.ok.length > 0 ? { recipients: pasted.ok } : null;
  }, [state.audienceMode, state.contactTag, state.segmentId, pasted.ok]);

  /*
   * The one source of truth for how many, how much, and whether it may go.
   *
   * Asked on the audience step without a template (counts only) and again on
   * the review step with one (counts plus the quote against the wallet). The
   * console works nothing out for itself: the count is the roll the create
   * route would queue, and `refusal` is the refusal create or start would give.
   */
  const previewBody = useMemo(() => {
    if (!audienceBody) return null;
    // An SMS has no template: the kind decides who is reachable and the text
    // decides the parts each message takes, so both go with the audience.
    if (isSms) {
      return {
        ...audienceBody,
        channel: 'sms' as const,
        sms_kind: state.smsKind,
        ...(smsText ? { sms_body: smsText } : {}),
        sample_size: 3
      } satisfies broadcasts.PreviewBroadcastRequest;
    }
    // An email has no template either: the kind decides who is reachable, and
    // the subject and text are what the server lays out for the preview.
    if (isEmail) {
      return {
        ...audienceBody,
        channel: 'email' as const,
        email_kind: state.emailKind,
        ...(emailSubject ? { email_subject: emailSubject } : {}),
        ...(emailBody ? { email_body: emailBody } : {}),
        ...emailButton,
        sample_size: 3
      } satisfies broadcasts.PreviewBroadcastRequest;
    }
    return {
      ...audienceBody,
      ...(template ? {
        template_code: template.code,
        template_category: template.category ?? undefined,
        template_language: template.language,
        // The review step's "English (US): 120, Afrikaans: 30", counted by the server over the real roll.
        ...(perLanguage ? { per_recipient_language: true } : {})
      } : {}),
      ...(vars.length > 0 ? { variables: JSON.stringify(state.variables) } : {}),
      sample_size: 3
    } satisfies broadcasts.PreviewBroadcastRequest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceBody, template, vars.length, state.variables, isSms, state.smsKind, smsText,
    isEmail, state.emailKind, emailSubject, emailBody, emailButtonLabel, emailButtonUrl, perLanguage]);

  const previewQ = useQuery({
    enabled: previewBody !== null,
    queryKey: ['broadcasts', 'preview', JSON.stringify(previewBody)],
    queryFn: () => broadcasts.previewBroadcast(previewBody!),
    placeholderData: (prev) => prev,
    retry: false,
    staleTime: 15_000
  });
  const preview = previewQ.data?.data ?? null;

  // ── The saved draft ─────────────────────────────────────────────────────

  const draftQ = useQuery({
    queryKey: ['broadcasts', 'draft'],
    queryFn: () => broadcasts.getBroadcastDraft(),
    retry: false,
    staleTime: Infinity
  });

  // Loaded once, on the way in. Re-applying it later would fight the person's
  // typing every time the query refreshed.
  useEffect(() => {
    if (loadedDraft.current || draftQ.isPending) return;
    loadedDraft.current = true;
    if (draftQ.isError) { setDraftBroken(true); return; }
    const saved = draftQ.data?.data;
    if (!saved?.state) return;
    setState(fromSaved(saved.state));
    setSavedAt(new Date(saved.updated_at).getTime());
    notifySuccess(t('broadcasts.new.draft.resumed'), t('broadcasts.new.title'));
  }, [draftQ.isPending, draftQ.isError, draftQ.data, t]);

  // Written after the typing stops, not on every keystroke.
  useEffect(() => {
    if (sent || changedAt === null || !loadedDraft.current) return;
    const handle = window.setTimeout(() => {
      broadcasts.saveBroadcastDraft(toSaved(state))
        .then(() => { setSavedAt(Date.now()); setDraftBroken(false); })
        // A draft that cannot be kept is said plainly, once, and the page falls
        // back to warning on the way out. It is never invented in the browser:
        // a broadcast half-written in localStorage on one machine is worse than
        // no draft at all.
        .catch(() => setDraftBroken(true));
    }, DRAFT_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [state, changedAt, sent]);

  /** Whether the last edit has reached the server yet. Drives the header line. */
  const pendingSave = draftBroken || savedAt === null || (changedAt ?? 0) > savedAt;

  /*
   * The guard is armed for the whole time there is an unsent broadcast on this
   * page, not only while a save is in flight.
   *
   * Precision was tried first (`dirty` true only between a keystroke and the
   * write that follows it) and it is wrong for this hook: `useUnsavedGuard`
   * pushes a history sentinel on every rising edge and pops it on every falling
   * one, and `history.back()` is asynchronous, so a draft that saves every
   * second or so produces a popstate that lands after the guard has re-armed
   * and asks the question with nobody having tried to leave. Latched, the
   * sentinel is pushed once and popped once.
   *
   * The copy carries the precision instead, and tells the truth either way: the
   * draft is kept, unless it is not, in which case leaving really does lose it.
   */
  const dirty = !sent && hasContent(state);
  useUnsavedGuard(dirty, {
    title: t('broadcasts.new.leave.title'),
    body: draftBroken ? t('broadcasts.new.leave.body.unsaved') : t('broadcasts.new.leave.body'),
    discard: t('broadcasts.new.leave.discard'),
    keep: t('broadcasts.new.leave.keep')
  });

  // ── Validation, continuously ────────────────────────────────────────────

  /**
   * What is wrong with a step right now.
   *
   * Recomputed on every render rather than only on Next, so a field that has
   * been fixed stops complaining and the stepper knows which steps may be
   * jumped to. The server still decides: this only stops somebody walking
   * forward into a refusal it would have given anyway.
   */
  const problems = useMemo(() => {
    const audience: Record<string, string> = {};
    if (!isSms && !isEmail && !state.phoneNumberId) audience.phoneNumberId = t('broadcasts.form.phoneNumberRequired');
    if (state.audienceMode === 'tag' && !state.contactTag) audience.tag = t('broadcasts.form.tagRequired');
    if (state.audienceMode === 'segment' && !state.segmentId) audience.segment = t('broadcasts.form.segmentRequired');
    if (state.audienceMode === 'paste') {
      if (pasted.ok.length === 0) {
        audience.recipients = isEmail ? t('broadcasts.email.recipientsRequired') : t('broadcasts.form.recipientsRequired');
      } else if (pasted.bad.length > 0) {
        audience.recipients = t(isEmail ? 'broadcasts.email.recipientsUnreadable' : 'broadcasts.form.recipientsUnreadable', {
          count: pasted.bad.length, sample: pasted.bad.slice(0, 3).join(', ')
        });
      }
    }
    // Whatever the server says about this audience wins over anything the
    // browser might think, and it is shown against the field that caused it.
    if (audienceBody && preview?.refusal && preview.refusal.code !== 'INSUFFICIENT_CREDITS') {
      const field = state.audienceMode === 'tag' ? 'tag' : state.audienceMode === 'segment' ? 'segment' : 'recipients';
      audience[field] = t(`broadcasts.new.refusal.${preview.refusal.code}`, preview.refusal.message);
    }

    const message: Record<string, string> = {};
    if (isEmail) {
      if (!state.emailSubject.trim()) message.emailSubject = t('broadcasts.email.subjectRequired');
      if (!state.emailBody.trim()) message.emailBody = t('broadcasts.email.bodyRequired');
      const label = state.emailButtonLabel.trim();
      const url = state.emailButtonUrl.trim();
      if (url && !label) message.emailButtonLabel = t('broadcasts.email.button.labelRequired');
      if (label && !url) message.emailButtonUrl = t('broadcasts.email.button.urlRequired');
      else if (url && !isButtonLink(url)) message.emailButtonUrl = t('broadcasts.email.button.urlInvalid');
    } else if (isSms) {
      if (!state.smsBody.trim()) message.smsBody = t('broadcasts.sms.bodyRequired');
      else if (smsCount && smsCount.segments > smsCount.max_segments) {
        message.smsBody = t('broadcasts.sms.tooLong', { max: smsCount.max_segments });
      }
    } else {
      if (!state.templateCode) message.templateCode = t('broadcasts.form.templateRequired');
      if (mediaHeader && !state.headerMediaUrl.trim()) message.headerMedia = t('broadcasts.form.headerMediaRequired');
      for (const v of vars) {
        if (!state.variables[v]?.trim()) message[`var-${v}`] = t('broadcasts.form.variableRequired');
      }
    }

    const when: Record<string, string> = {};
    if (state.sendMode === 'schedule') {
      const at = scheduledInstant(state.sendAt, timezone);
      if (!at) when.sendAt = t('broadcasts.new.when.required');
      else if (at.getTime() < Date.now() - 60_000) when.sendAt = t('broadcasts.new.when.past');
    }

    return [audience, message, when, {}];
  }, [state, pasted, vars, mediaHeader, preview, audienceBody, timezone, t, isSms, smsCount, isEmail]);

  const stepOk = (n: number) => Object.keys(problems[n] ?? {}).length === 0;
  const canReview = stepOk(0) && stepOk(1) && stepOk(2);
  /** Send is offered only when the server has already said it would take it. */
  const canSend = canReview && preview !== null && !preview.refusal && preview.reachable > 0 &&
    // For SMS, only once the preview is an SMS one for the text as it stands.
    (!isSms || (preview.channel === 'sms' && smsText === state.smsBody.trim())) &&
    // For email, only once the preview is an email one for the email as it stands.
    (!isEmail || (preview.channel === 'email' && emailSettled));

  function goTo(next: number) {
    if (next > state.step) {
      // Every step between here and there has to be sound, so nobody skips
      // forward past an unanswered question.
      for (let i = state.step; i < next; i++) {
        if (!stepOk(i)) { setErrors(problems[i]); set('step', i); return; }
      }
    }
    setErrors({});
    set('step', next);
  }

  // ── Sending ─────────────────────────────────────────────────────────────

  async function send() {
    if (!canSend || !audienceBody || (!isSms && !isEmail && !template)) return;
    setSending(true);
    try {
      const at = state.sendMode === 'schedule' ? scheduledInstant(state.sendAt, timezone) : null;
      const created = await broadcasts.createBroadcast(isEmail ? {
        // No number and no template: the platform lays the text out as an
        // email from the business, with the unsubscribe link on marketing.
        channel: 'email',
        email_subject: state.emailSubject.trim(),
        email_body: state.emailBody.trim(),
        ...(state.emailButtonLabel.trim() && state.emailButtonUrl.trim()
          ? { email_button_label: state.emailButtonLabel.trim(), email_button_url: state.emailButtonUrl.trim() }
          : {}),
        email_kind: state.emailKind,
        ...audienceBody,
        throttle_per_second: state.throttle,
        ...(at ? { scheduled_at: at.toISOString() } : {})
      } : isSms ? {
        // No number and no template: the text goes through the platform's SMS
        // provider with the business name in front of it.
        channel: 'sms',
        sms_body: state.smsBody.trim(),
        sms_kind: state.smsKind,
        ...audienceBody,
        throttle_per_second: state.throttle,
        ...(at ? { scheduled_at: at.toISOString() } : {})
      } : {
        phone_number_id: state.phoneNumberId,
        template_code: template!.code,
        // Derived from the chosen template, never typed: a language that does
        // not match the approved template is refused per recipient by Meta.
        template_language: template!.language,
        // Each person is sent the approved version in their language, else the one above.
        per_recipient_language: perLanguage,
        template_category: template!.category ?? undefined,
        ...audienceBody,
        ...(vars.length > 0 ? { variables: JSON.stringify(state.variables) } : {}),
        ...(mediaHeader && state.headerMediaUrl.trim() ? { header_media_url: state.headerMediaUrl.trim() } : {}),
        throttle_per_second: state.throttle,
        ...(at ? { scheduled_at: at.toISOString() } : {})
      });

      // "Send now" means now. Without this the broadcast sits scheduled until
      // somebody finds it in the list and presses Start, which is what the old
      // drawer did while its button said "Create broadcast".
      if (!at) {
        try {
          await broadcasts.startBroadcast(created.data.id);
        } catch (e) {
          // More recipients than the number may reach today (2 October 2026).
          // The broadcast is saved; its page says why and offers to send as
          // far as the limit allows.
          if (!isDailyLimit(e)) throw e;
          await broadcasts.discardBroadcastDraft().catch(() => { /* the broadcast is saved */ });
          setSent(true);
          qc.invalidateQueries({ queryKey: ['broadcasts'] });
          notifyError(startErrorMessage(t, e), t('broadcasts.start.dailyLimitTitle'));
          nav(`/broadcasts/${created.data.id}`);
          return;
        }
      }

      setSent(true);
      await broadcasts.discardBroadcastDraft().catch(() => { /* the send is what matters */ });
      qc.invalidateQueries({ queryKey: ['broadcasts'] });
      notifySuccess(
        at
          ? t('broadcasts.new.sent.scheduled', { count: created.data.recipients_total })
          : t('broadcasts.new.sent.now', { count: created.data.recipients_total }),
        t('broadcasts.title')
      );
      nav(`/broadcasts/${created.data.id}`);
    } catch (e) {
      // The server refused after all: a wallet drained between the preview and
      // the press, or an audience that emptied. Its own words, its own figures.
      notifyError(startErrorMessage(t, e), t('broadcasts.toast.createFailed'));
      setSending(false);
    }
  }

  function discardDraft() {
    openConfirmModal({
      title: t('broadcasts.new.draft.discard.title'),
      children: t('broadcasts.new.draft.discard.body'),
      labels: { confirm: t('broadcasts.new.draft.discard'), cancel: t('action.back') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void broadcasts.discardBroadcastDraft().catch(() => { /* nothing to lose */ });
        setState(EMPTY_DRAFT);
        setChangedAt(null);
        setSavedAt(null);
        setErrors({});
      }
    });
  }

  const shared = { state, set, errors, t };

  return (
    <Stack gap="lg" className="rr-bc-new">
      <BreadcrumbTrail
        items={[
          { label: t('broadcasts.title'), href: '/broadcasts' },
          { label: t('broadcasts.new.title') }
        ]}
        renderLink={(href, label) => <Link to={href}>{label}</Link>}
      />

      <PageHeader
        title={t('broadcasts.new.title')}
        description={t('broadcasts.new.description')}
        withDivider={false}
        actions={
          <Group gap="xs">
            <HelpButton guide="audience" />
            {/* What is keeping this draft, said where the person is looking.
                Silence here would leave "is my work safe" unanswered on the one
                page where the answer matters. */}
            <Text role="caption" c="var(--rr-color-text-muted)" aria-live="polite">
              {draftBroken
                ? t('broadcasts.new.draft.unavailable')
                : pendingSave
                  ? t('broadcasts.new.draft.saving')
                  : savedAt
                    ? t('broadcasts.new.draft.saved', { time: formatTime(savedAt) })
                    : ''}
            </Text>
            {(savedAt !== null || hasContent(state)) && (
              <Button size="xs" variant="subtle" onClick={discardDraft}>{t('broadcasts.new.draft.discard')}</Button>
            )}
          </Group>
        }
      />

      <div data-guide="broadcast-steps">
        <Stepper active={state.step} onStepClick={goTo}>
          {STEPS.map((s) => <Stepper.Step key={s} label={t(`broadcasts.new.step.${s}`)} />)}
        </Stepper>
      </div>

      {previewQ.isError && <Alert color="warning" role="alert">{t('broadcasts.new.previewFailed')}</Alert>}

      <div data-guide="broadcast-wizard" className="rr-bc-new__wizard">
      <Card withBorder className="rr-bc-new__panel">
        {state.step === 0 && (
          <AudienceStep
            {...shared}
            smsAvailable={smsAvailable}
            emailAvailable={emailAvailable}
            numbers={numbers}
            numbersLoading={numbersQ.isPending}
            tags={tagsQ.data?.data ?? []}
            tagsLoading={tagsQ.isPending}
            segments={segmentsQ.data?.data ?? []}
            preview={preview}
            previewing={previewQ.isFetching}
            pastedCount={pasted.ok.length}
            maxPasted={MAX_PASTED_CHARS}
          />
        )}
        {state.step === 1 && isSms && (
          <SmsMessageStep
            {...shared}
            count={smsCount}
            counting={smsCountQ.isFetching}
            preview={preview}
            previewing={previewQ.isFetching}
            text={smsText}
          />
        )}
        {state.step === 1 && isEmail && (
          <EmailMessageStep
            {...shared}
            preview={preview}
            previewing={previewQ.isFetching}
            settled={emailSettled}
          />
        )}
        {state.step === 1 && !isSms && !isEmail && (
          <MessageStep
            {...shared}
            sendable={sendable}
            template={template}
            versions={versions}
            perLanguage={perLanguage}
            templatesLoading={templatesQ.isPending}
            blockedByDevFallback={blockedByDevFallback}
            components={parsed}
            vars={vars}
            mediaHeader={mediaHeader}
            preview={preview}
            previewing={previewQ.isFetching}
          />
        )}
        {state.step === 2 && <WhenStep {...shared} timezone={timezone} />}
        {state.step === 3 && (
          <ReviewStep
            {...shared}
            timezone={timezone}
            template={template}
            perLanguage={perLanguage}
            isSms={isSms}
            isEmail={isEmail}
            preview={preview}
            previewing={previewQ.isFetching}
            audienceLabel={
              state.audienceMode === 'tag' ? state.contactTag
                : state.audienceMode === 'segment'
                  ? (segmentsQ.data?.data ?? []).find((s) => s.id === state.segmentId)?.name ?? state.segmentId
                  : isEmail ? t('broadcasts.email.audience.paste') : t('broadcasts.form.audience.paste')
            }
            number={numbers.find((n) => n.id === state.phoneNumberId) ?? null}
          />
        )}
      </Card>

      <div className="rr-bc-new__foot">
        <Button
          variant="default"
          onClick={() => (state.step === 0 ? nav('/broadcasts') : goTo(state.step - 1))}
        >
          {state.step === 0 ? t('action.cancel') : t('action.back')}
        </Button>

        {state.step < LAST ? (
          <Button onClick={() => goTo(state.step + 1)} disabled={!stepOk(state.step)}>
            {t('action.next')}
          </Button>
        ) : (
          /*
           * This IS the confirmation. There is deliberately no second modal on
           * top of it: the whole step exists to state the count, the cost and
           * the balance, and asking "are you sure" after a person has read all
           * three teaches them to click through both.
           */
          <Button onClick={send} loading={sending} disabled={!canSend}>
            {state.sendMode === 'schedule' ? t('broadcasts.new.action.schedule') : t('broadcasts.new.action.send')}
          </Button>
        )}
      </div>
      </div>
    </Stack>
  );
}
